import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

const FLAGS: Record<string, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  ar: '🇸🇦',
};

export const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const changeLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng).then(() => {
      document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
      document.documentElement.className = lng === 'ar' ? 'font-arabic' : '';
      localStorage.setItem('i18nextLng', lng);
      setDropdownOpen(false);
    });
  }, [i18n]);

  const getLanguageLabel = (code: string) => {
    switch(code) {
      case 'en': return 'English';
      case 'fr': return 'Français';
      case 'ar': return 'العربية';
      default: return code;
    }
  };

  const getCurrentLanguageLabel = () => {
    switch(i18n.language) {
      case 'en': return 'English';
      case 'fr': return 'Français';
      case 'ar': return 'العربية';
      default: return 'English';
    }
  };

  const getCurrentFlag = () => FLAGS[i18n.language] || '🌐';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setDropdownOpen((open) => !open);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  return (
    <div className="relative group" role="navigation" aria-label="Language selector">
      <button 
        className="flex items-center space-x-2 text-white hover:text-primary transition-colors duration-300 font-semibold px-3 py-2 rounded-lg bg-stone-800/80 border border-stone-700 shadow-md"
        aria-label="Select language"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen ? 'true' : 'false'}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        onBlur={() => setDropdownOpen(false)}
        onKeyDown={handleKeyDown}
      >
        <span className="text-xl">{getCurrentFlag()}</span>
        <span className="hidden sm:inline">{getCurrentLanguageLabel()}</span>
      </button>
      <div className={`absolute top-full right-0 mt-2 bg-stone-800 rounded-lg shadow-xl transition-all duration-300 min-w-[140px] z-50 transform origin-top-right border border-stone-700 ${dropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} role="listbox" aria-label="Language options">
        {['en', 'fr', 'ar'].map((lang) => (
          <button
            key={lang}
            onClick={() => changeLanguage(lang)}
            className={`flex items-center gap-2 w-full text-left px-4 py-2 rounded-lg transition-colors duration-200 font-medium text-lg shadow-soft border border-stone-700 bg-white/80 hover:bg-stone-200
              ${i18n.language === lang ? 'bg-black text-white border-primary ring-2 ring-primary font-bold' : 'text-stone-800'}`}
            aria-label={`Change language to ${getLanguageLabel(lang)}`}
            role="option"
            aria-selected={i18n.language === lang}
            tabIndex={0}
          >
            <span className="text-xl">{FLAGS[lang]}</span>
            <span>{getLanguageLabel(lang)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};